import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// Uploads all selected files in parallel immediately on file selection,
// before form submit. Files land directly on DigitalOcean Spaces via
// presigned URLs — zero bytes through Rails. Injects hidden signed_id
// inputs so the form submits blob references instead of raw files.
export default class extends Controller {
  static targets = ["input", "progressList"]

  connect() {
    this.pendingUploads = 0
  }

  handleFileSelect(event) {
    const input = this.inputTarget
    const files = Array.from(input.files)
    if (files.length === 0) return

    const url = input.dataset.directUploadUrl || '/rails/active_storage/direct_uploads'
    const fieldName = input.name

    // Prevent Rails ActiveStorage form handler from re-uploading on submit
    delete input.dataset.directUploadUrl

    // Clear native file selection — form won't send raw bytes
    input.value = ''

    this.pendingUploads += files.length
    this.updateSubmitButton()

    // Upload all files simultaneously
    files.forEach(file => this.uploadFile(file, fieldName, url))
  }

  uploadFile(file, fieldName, url) {
    const itemEl = this.addProgressItem(file.name)

    const upload = new DirectUpload(file, url, {
      directUploadWillStoreFileWithXHR: (xhr) => {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            this.updateProgressItem(itemEl, Math.round(event.loaded / event.total * 100))
          }
        })
      }
    })

    upload.create((error, blob) => {
      this.pendingUploads--
      if (error) {
        this.failProgressItem(itemEl, file.name)
      } else {
        this.completeProgressItem(itemEl, file.name)
        this.injectBlobInput(fieldName, blob.signed_id)
      }
      this.updateSubmitButton()
    })
  }

  injectBlobInput(fieldName, signedId) {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = fieldName
    input.value = signedId
    this.element.appendChild(input)
  }

  updateSubmitButton() {
    const form = this.element.closest('form')
    if (!form) return

    const buttons = form.querySelectorAll('[type="submit"]')
    buttons.forEach(btn => {
      if (this.pendingUploads > 0) {
        if (!btn.dataset.parallelUploadDisabled) {
          btn.dataset.parallelUploadDisabled = 'true'
          btn.dataset.parallelUploadOriginal = btn.innerHTML
        }
        btn.disabled = true
        btn.innerHTML = `
          <span class="material-symbols-outlined animate-spin !text-lg align-middle">progress_activity</span>
          ${t('javascript.controllers.parallel_upload_controller.uploading_files', { count: this.pendingUploads })}
        `
        btn.classList.add('opacity-75', 'cursor-not-allowed')
      } else if (btn.dataset.parallelUploadDisabled) {
        btn.disabled = false
        btn.innerHTML = btn.dataset.parallelUploadOriginal
        delete btn.dataset.parallelUploadDisabled
        delete btn.dataset.parallelUploadOriginal
        btn.classList.remove('opacity-75', 'cursor-not-allowed')
      }
    })
  }

  addProgressItem(filename) {
    if (!this.hasProgressListTarget) return null
    this.progressListTarget.classList.remove('hidden')

    const item = document.createElement('div')
    item.className = 'flex items-center gap-2 p-2 rounded bg-blue-500/10 border border-blue-500/20 text-xs'
    item.innerHTML = `
      <span class="material-symbols-outlined !text-sm text-blue-400 animate-spin shrink-0">progress_activity</span>
      <span class="flex-1 truncate min-w-0 text-blue-300">${this.escapeHtml(filename)}</span>
      <div class="w-16 h-1.5 bg-white/10 rounded-full overflow-hidden shrink-0">
        <div class="h-full bg-blue-400 rounded-full transition-all duration-100 progress-bar" style="width:0%"></div>
      </div>
      <span class="w-8 text-right text-blue-400 shrink-0 progress-pct">0%</span>
    `
    this.progressListTarget.appendChild(item)
    return item
  }

  updateProgressItem(item, pct) {
    if (!item) return
    const bar = item.querySelector('.progress-bar')
    const label = item.querySelector('.progress-pct')
    if (bar) bar.style.width = `${pct}%`
    if (label) label.textContent = `${pct}%`
  }

  completeProgressItem(item, filename) {
    if (!item) return
    item.className = 'flex items-center gap-2 p-2 rounded bg-green-500/10 border border-green-500/20 text-xs'
    item.innerHTML = `
      <span class="material-symbols-outlined !text-sm text-green-400 shrink-0">check_circle</span>
      <span class="flex-1 truncate min-w-0 text-green-300">${this.escapeHtml(filename)}</span>
      <span class="text-green-400 shrink-0">${t('javascript.controllers.parallel_upload_controller.ready')}</span>
    `
  }

  failProgressItem(item, filename) {
    if (!item) return
    item.className = 'flex items-center gap-2 p-2 rounded bg-red-500/10 border border-red-500/20 text-xs'
    item.innerHTML = `
      <span class="material-symbols-outlined !text-sm text-red-400 shrink-0">error</span>
      <span class="flex-1 truncate min-w-0 text-red-300">${this.escapeHtml(filename)}</span>
      <span class="text-red-400 shrink-0">${t('javascript.controllers.parallel_upload_controller.failed_reselect')}</span>
    `
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
